import axios from "axios";
import axiosRetry, { exponentialDelay, isIdempotentRequestError, isRetryableError, retryAfter } from "axios-retry";
import { ApiHeaders } from "../utils/constants";
import { redirectToBffLogin } from "./bffAuth";

export const RetryAfterBudgetMs = 15000;

const isRateLimited = (error) =>
	Boolean(error?.config?.method) && error?.response?.status === 429 && isRetryableError(error);

export const shouldRetry = (error) =>
	(isIdempotentRequestError(error) || isRateLimited(error)) && retryAfter(error) <= RetryAfterBudgetMs;

export const shouldRetryRateLimited = (error) =>
	isRateLimited(error) && retryAfter(error) <= RetryAfterBudgetMs;

// Single construction point for the storefront's API axios instances (withCredentials, baseURL,
// Content-type, the ahwid tenant header, and the X-CSRF marker the BFF requires on state-changing
// requests; optional axiosRetry).
// Do NOT use this for the direct-to-blob SAS uploads (entityMediaController.uploadFileToSas): they must stay
// bare axios, since Azure Storage CORS rejects credentialed/ahwid preflights.
export const RetryPolicy = {
	Default: { retries: 3, retryDelay: exponentialDelay, retryCondition: shouldRetry },
	NetworkErrorsOnly: { retries: 1, retryDelay: axiosRetry.exponentialDelay, retryCondition: axiosRetry.isNetworkError },
	RateLimitedOnly: { retries: 3, retryDelay: exponentialDelay, retryCondition: shouldRetryRateLimited },
	None: null,
};

export const createApiInstance = ({ baseURL, retry = false, contentType = "application/json", headers: extraHeaders = {}, params: defaultParams = {} }) => {
	const instance = axios.create({
		withCredentials: true,
		baseURL,
		headers: {
			"Content-type": contentType,
			"X-CSRF": "1",
			[ApiHeaders.WebsiteId]: process.env.REACT_APP_WEBSITE_ID,
			...extraHeaders,
		},
		params: defaultParams,
	});

	if (retry) {
		axiosRetry(instance, retry === true ? RetryPolicy.Default : retry);
	}

	// A cookie-session 401 means the session expired or is absent (the BFF maps permission denials to 403,
	// not 401), so re-authenticate rather than surfacing a generic error. The /auth/me probe uses its own
	// bare axios, so it is not affected by this interceptor.
	instance.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error?.response?.status === 401) {
				redirectToBffLogin();
			}
			return Promise.reject(error);
		},
	);

	return instance;
};
